import { ISelectOption } from '@private/components';

import { IParentOption, ITreeItem } from 'components/Transfers/TransferTree/types';
import { ORGANIZATION } from 'constants/common';
import { alphabetSort } from 'utils/common';
import { normalizeBooleanFields } from 'utils/commonNormalizer';
import { INullable, TObject } from 'utils/types';

import {
  IApiUser,
  ICascadingSchema,
  ICascadingSchemaDetails,
  ICustomAction,
  IData,
  IGetMerchantOutputChannelsResult,
  IGetMerchantRoutingRulesetsResult,
  ILogoData,
  IMerchant,
  IMerchantClientsLimits,
  IMerchantDetails,
  IMerchantRequiredFields,
  IMerchantRouting,
  IMerchantRoutingSchemas,
  IMerchantsScope,
  IMerchantsScopeData,
  IMerchantWebhooksEvents,
  IOutputChannel,
  IPaymentPageOptions,
  IRoutingSchema,
  IWithdrawalRequestRequiredFields,
  TCustomActionData,
  TMerchantDetailsData,
  TMerchantNames,
} from './types';

export type IMerchantData = TObject & {
  hqLegalEntity: TObject;
  merchant: TObject;
};

export const normalizeMerchant = (data: IMerchantData): IMerchant => {
  return {
    antifraudProvider: (data?.antifraudProvider as string) || '',
    apiAccessMode: (data?.apiAccessMode as string) || '',
    cardholderTransliteration: !!data?.cardholderTransliteration,
    changePaymentMethod: !!data?.changePaymentMethod,
    created: (data?.created as string) || '',
    customerIdNormalization: (data?.customerIdNormalization as string) || '',
    email: (data?.email as string) || '',
    enableCardVerification: !!data?.enableCardVerification,
    enableCardVerificationBlock: !!data?.enableCardVerificationBlock,
    enableCascading: !!data?.enableCascading,
    enableCashier: !!data?.enableCashier,
    enableCPMGroups: !!data?.enableCPMGroups,
    enabled: !!data?.enabled,
    enableHppCascading: !!data?.enableHppCascading,
    fillCardholderName: !!data?.fillCardholderName,
    hqLegalEntity: {
      id: data?.hqLegalEntity?.id as number,
      name: (data?.hqLegalEntity?.name as string) || '',
    },
    id: (data?.id as number) || 0,
    logoFileName: (data?.logoFileName as string) || '',
    merchant: {
      id: data?.merchant?.id as number,
      name: (data?.merchant?.name as string) || '',
    },
    name: (data?.name as string) || '',
    platform: (data?.platform as string) || '',
    preMemorisedRouting: !!data?.preMemorisedRouting,
    repeatCallbackOnError: !!data?.repeatCallbackOnError,
    restrictedCountries: (data?.restrictedCountries as string[]) || [],
    returnCancelUrl: (data?.returnCancelUrl as string) || '',
    returnCashierUrl: (data?.returnCashierUrl as string) || '',
    returnFailureUrl: (data?.returnFailureUrl as string) || '',
    returnHoldUrl: (data?.returnHoldUrl as string) || '',
    returnInProgressTimeout: (data?.returnInProgressTimeout as string) || '',
    returnInProgressUrl: (data?.returnInProgressUrl as string) || '',
    returnPayoutFailureUrl: (data?.returnPayoutFailureUrl as string) || '',
    returnSuccessUrl: (data?.returnSuccessUrl as string) || '',
    shopUrl: (data?.shopUrl as string) || '',
    trustedSortingPriority: !!data?.trustedSortingPriority,
    useCardToken: !!data?.useCardToken,
    useClientMaxStatsForRouting: !!data?.useClientMaxStatsForRouting,
    useClientStatsForRouting: !!data?.useClientStatsForRouting,
    useRoutingPriority: !!data?.useRoutingPriority,
    webhookUrl: (data?.webhookUrl as string) || '',
  };
};
export const normalizeCascadingSchema = (data: TObject): ICascadingSchema => {
  return {
    active: !!data?.active,
    id: (data?.id as number) ?? null,
    name: (data?.name as string) || '',
  };
};

export const normalizeRoutingSchema = (data: TObject): IRoutingSchema => {
  return {
    active: !!data?.active,
    deleted: !!data?.deleted,
    id: (data?.id as number) ?? null,
    name: (data?.name as string) || '',
  };
};

const normalizeData = (data: TObject): INullable<IData> => {
  if (!data) {
    return null;
  }
  return {
    data: (data?.data as string) || null,
    type: (data?.type as string) || null,
  };
};

const normalizeLogoData = (data: TObject): INullable<ILogoData> => {
  if (!data) {
    return null;
  }
  return {
    data: (data?.data as string) || null,
    svg: (data?.svg as string) || null,
    type: (data?.type as string) || null,
  };
};

export const normalizeMerchantDetails = (
  data: TMerchantDetailsData,
): IMerchantDetails => {
  return {
    advancedSettings: {
      antifraudApiKey: (data?.advancedSettings.antifraudApiKey as string) || null,
      antifraudDepositBlockTraffic: !!data?.advancedSettings.antifraudDepositBlockTraffic,
      antifraudDepositFinalStatuses:
        (data?.advancedSettings.antifraudDepositFinalStatuses as string[]) || null,
      antifraudDepositMode:
        (data?.advancedSettings.antifraudDepositMode as string) || null,
      antifraudProvider: (data?.advancedSettings.antifraudProvider as string) || null,
      antifraudUrl: (data?.advancedSettings?.antifraudUrl as string) || null,
      antifraudWithdrawalBlockTraffic:
        !!data?.advancedSettings.antifraudWithdrawalBlockTraffic,
      antifraudWithdrawalFinalStatuses:
        (data?.advancedSettings.antifraudWithdrawalFinalStatuses as string[]) || null,
      antifraudWithdrawalMode:
        (data?.advancedSettings.antifraudWithdrawalMode as string) || null,
      customerIdNormalization:
        (data?.advancedSettings?.customerIdNormalization as string) || null,
      emails: (data?.advancedSettings?.emails as string) || null,
      motoAmount: (data?.advancedSettings?.motoAmount as string) ?? null,
      motoCurrency: (data?.advancedSettings?.motoCurrency as string) || null,
      motoDescription: (data?.advancedSettings?.motoDescription as string) || null,
      motoTID: (data?.advancedSettings?.motoTID as string) || null,
      phone: (data?.advancedSettings?.phone as string) || null,
      phoneCode: (data?.advancedSettings?.phoneCode as string) || null,
      restrictedCountries:
        (data?.advancedSettings?.restrictedCountries as string[]) || null,
      sendEmailToMerchant: !!(data?.advancedSettings?.sendEmailToMerchant as string),
      skype: (data?.advancedSettings?.skype as string) || null,
    },
    cardVerification: {
      enableCardVerification: !!data?.cardVerification?.enableCardVerification,
      enableCardVerificationBlock: !!data?.cardVerification?.enableCardVerificationBlock,
    },
    gPayMerchantBlock: {
      gpayMerchantId: (data?.gPayMerchantBlock?.gpayMerchantId as string) || null,
      gpayMerchantName: (data?.gPayMerchantBlock?.gpayMerchantName as string) || null,
      gpayMerchantOrigin: (data?.gPayMerchantBlock?.gpayMerchantOrigin as string) || null,
    },
    merchantTerminalInfo: {
      apiAccessMode: (data?.merchantTerminalInfo?.apiAccessMode as string) || null,
      baseCurrency: (data?.merchantTerminalInfo?.baseCurrency as string) || null,
      description: (data?.merchantTerminalInfo?.description as string) || null,
      enableCashier: !!(data?.merchantTerminalInfo?.enableCashier as string),
      enabled: !!data?.merchantTerminalInfo?.enabled,
      id: (data?.merchantTerminalInfo?.id as number) || 0,
      legalEntityId: (data?.merchantTerminalInfo?.legalEntityId as number) || null,
      legalEntityName: (data?.merchantTerminalInfo?.legalEntityName as string) || null,
      merchantId: (data?.merchantTerminalInfo?.merchantId as number) || null,
      merchantName: (data?.merchantTerminalInfo?.merchantName as string) || null,
      name: (data?.merchantTerminalInfo?.name as string) || null,
      platform: (data?.merchantTerminalInfo?.platform as string) || null,
      secretKey: (data?.merchantTerminalInfo?.secretKey as string) || null,
      shopTitle: (data?.merchantTerminalInfo?.shopTitle as string) || null,
      useCardToken: !!data?.merchantTerminalInfo?.useCardToken,
      withdrawalCardLimit:
        (data?.merchantTerminalInfo?.withdrawalCardLimit as number) || null,
    },
    paymentPageOptions: data?.paymentPageOptions
      ? normalizeBooleanFields<IPaymentPageOptions>(data?.paymentPageOptions)
      : null,
    paymentRequestRequiredFields: normalizeBooleanFields<IMerchantRequiredFields>(
      data?.paymentRequestRequiredFields,
    ),
    paymentStatusUrl: {
      inProgressTimeout: (data?.paymentStatusUrl?.inProgressTimeout as number) ?? null,
      returnCancelUrl: (data?.paymentStatusUrl?.returnCancelUrl as string) || null,
      returnFailureUrl: (data?.paymentStatusUrl?.returnFailureUrl as string) || null,
      returnFromCashierUrl:
        (data?.paymentStatusUrl?.returnFromCashierUrl as string) || null,
      returnHoldUrl: (data?.paymentStatusUrl?.returnHoldUrl as string) || null,
      returnInProgressUrl:
        (data?.paymentStatusUrl?.returnInProgressUrl as string) || null,
      returnPayoutFailureUrl:
        (data?.paymentStatusUrl?.returnPayoutFailureUrl as string) || null,
      returnSuccessUrl: (data?.paymentStatusUrl?.returnSuccessUrl as string) || null,
      shopUrl: (data?.paymentStatusUrl?.shopUrl as string) || null,
    },
    routing: normalizeBooleanFields<IMerchantRouting>(data?.routing),
    sendWebhooksEventsView: {
      ...normalizeBooleanFields<Omit<IMerchantWebhooksEvents, 'webhookUrl'>>(
        data?.sendWebhooksEventsView,
      ),
      webhookUrl: (data?.sendWebhooksEventsView?.webhookUrl as string) || null,
    },
    uiSettings: {
      billingDescriptor: (data?.uiSettings?.billingDescriptor as string) || null,
      cashierCss: normalizeData(data?.uiSettings?.cashierCss),
      cashierTheme: (data?.uiSettings?.cashierTheme as string) || null,
      enableCPMGroups: !!data?.uiSettings?.enableCPMGroups,
      logo: normalizeLogoData(data?.uiSettings?.logo),
      ppCss: normalizeData(data?.uiSettings?.ppCss),
      ppTheme: (data?.uiSettings?.ppTheme as string) || null,
    },
    withdrawalRequestRequiredFields:
      normalizeBooleanFields<IWithdrawalRequestRequiredFields>(
        data?.withdrawalRequestRequiredFields,
      ),
  };
};

export const normalizeCascadingSchemaDetails = (
  data: TObject,
): ICascadingSchemaDetails => {
  return {
    autoSubmitCountdown: data?.countdownTimer as number,
    cascadeOnDeclineAfterRedirect: !!data?.cascadeOnDeclineAfterRedirect,
    cascadeOnDeclineBeforeRedirect: !!data?.cascadeOnDeclineBeforeRedirect,
    filterOutNonTrustedRulesets: !!data?.filterNonTrusted,
    filterOutRulesetsWithFailTimers: !!data?.filterFailTimers,
    name: (data?.name as string) || '',
    priorityType: (data?.priorityType as string) || '',
    retryAttempts: data?.retryAttempts as number,
  };
};

export const normalizeNames = (data: TObject) => {
  return {
    label: (data?.name as string) || '',
    value: (data?.id as number) || 0,
  };
};

export const normalizeGroupNames = (data: TObject) => {
  return {
    label: (data?.groupName as string) || '',
    value: (data?.id as number) || 0,
  };
};

export const normalizeMerchantsNames = (data: TMerchantNames) => {
  return data?.listMerchantTerminalNames?.map(normalizeNames) || [];
};

const normalizeBaseTreeScopeEntity = (
  data: TObject,
  defaultName?: string,
): IParentOption => {
  return {
    id: (data?.id as number) || 0,
    name: (`${data?.id} ${data?.name}` as string) || defaultName || '',
  };
};

export const normalizeMerchantsTreeScope = (
  dataItem: IMerchantsScopeData,
): ITreeItem<'merchantTerminals'> => {
  const baseData = normalizeBaseTreeScopeEntity(dataItem, ORGANIZATION);

  return {
    ...baseData,
    merchantTerminals:
      dataItem?.merchantTerminals?.map((item) => normalizeBaseTreeScopeEntity(item)) ||
      [],
  };
};

const normalizeBaseSelectScopeEntity = (data: TObject): ISelectOption => {
  return {
    label: (data?.name as string) || '',
    value: (data?.id as number) || 0,
  };
};

export const normalizeMerchantsSelectScope = (
  data?: IMerchantsScopeData[],
): IMerchantsScope => {
  const merchantsMap: IMerchantsScope['merchantsMap'] = {};

  const allMerchants: ISelectOption[] = [];
  const allMerchantsSet = new Set();

  if (!data) {
    return {
      allMerchants,
      merchantsMap,
      organizations: [],
    };
  }

  const organizations = data?.map((dataItem) => {
    const value = (dataItem?.id as number) || 0;

    if (merchantsMap[value]) {
      // eslint-disable-next-line no-console
      console.warn(
        'WARNING!!! There are several organizations with the same id in the list. It can cause unexpected bugs.',
      );
    }

    const merchantsList =
      dataItem?.merchantTerminals?.map((item) => {
        const normalizedItem = normalizeBaseSelectScopeEntity(item);
        const { value } = normalizedItem;
        if (value) {
          const setItemExists = allMerchantsSet.has(value);

          if (!setItemExists) {
            allMerchants.push(normalizedItem);
            allMerchantsSet.add(value);
          }
        }
        return normalizedItem;
      }) || [];
    merchantsMap[value] = merchantsList?.sort(alphabetSort);

    return {
      label: (dataItem?.name as string) || ORGANIZATION,
      value,
    };
  });

  const result = {
    allMerchants: allMerchants.sort(alphabetSort),
    merchantsMap,
    organizations: organizations?.sort(alphabetSort),
  };

  return result;
};

export const normalizeRoutingSchemas = (data: TObject): IMerchantRoutingSchemas => {
  return {
    id: (data?.id as number) || null,
    isActive: !!data?.deleted,
    name: (data?.name as string) || '',
  };
};

export const normalizedApiUser = (data: TObject): IApiUser => {
  return {
    created: (data?.created as string) || '',
    id: (data?.id as number) || 0,
    isActive: !!data?.isActive,
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
    unlockDate: (data?.unlockDate as string) || '',
    username: (data?.username as string) || '',
  };
};

export const normalizeMerchantClientsLimits = (data: TObject): IMerchantClientsLimits => {
  return {
    dailyLimit: (data?.dailyLimit as number) || null,
    id: (data?.id as number) || null,
    monthlyLimit: (data?.monthlyLimit as number) || null,
    weeklyLimit: (data?.weeklyLimit as number) || null,
  };
};

export const normalizeMerchantRulesets = (
  data: TObject[],
): IGetMerchantRoutingRulesetsResult => {
  const routingRulesets: IGetMerchantRoutingRulesetsResult['routingRulesets'] = [];
  const routingRulesetsMap: IGetMerchantRoutingRulesetsResult['routingRulesetsMap'] = {};

  data?.forEach((item) => {
    const normalizedRuleset = {
      label: (item?.name as string) || '',
      value: (item?.id as number) || 0,
    };
    routingRulesets.push(normalizedRuleset);
    routingRulesetsMap[normalizedRuleset.value as number] = normalizedRuleset;
  });

  return { routingRulesets, routingRulesetsMap };
};

export const normalizeMerchantOutputChannelsList = (
  data: TObject[],
): IGetMerchantOutputChannelsResult => {
  const outputChannels: IGetMerchantOutputChannelsResult['outputChannels'] = [];
  const outputChannelsMap: IGetMerchantOutputChannelsResult['outputChannelsMap'] = {};

  data?.forEach((item) => {
    const normalizedOutputChannel = {
      label: (item?.name as string) || '',
      value: (item?.id as number) || 0,
    };
    outputChannels.push(normalizedOutputChannel);
    outputChannelsMap[normalizedOutputChannel.value as number] = normalizedOutputChannel;
  });

  return { outputChannels, outputChannelsMap };
};

const normalizeChannel = (data: TObject): IOutputChannel => {
  return {
    id: (data?.id as number) || null,
    name: (data?.name as string) || null,
  };
};

export const normalizeCustomAction = (data: TCustomActionData): ICustomAction => {
  return {
    actionType: (data?.actionType as string) || null,
    created: (data?.created as string) || null,
    enabled: !!data?.enabled,
    feature: (data?.feature as string) || '',
    id: (data?.id as number) || null,
    merchantTerminal: {
      id: (data?.merchantTerminal?.id as number) || null,
    },
    name: (data?.name as string) || null,
    outputChannels: (data?.outputChannels || []).map(normalizeChannel) || [],
    triggerType: (data?.triggerType as string) || null,
    updated: (data?.updated as string) || null,
    webhookUrl: (data?.webhookUrl as string) || null,
  };
};
